import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext.jsx";
import { useNavigate } from "react-router-dom";
import CardBox from "../components/ui/CardBox.jsx";
import FormField from "../components/form/FormField.jsx";
import FormControl from "../components/form/FormControl.jsx";
import BaseButton from "../components/ui/BaseButton.jsx";
import BaseButtons from "../components/ui/BaseButtons.jsx";
import BaseDivider from "../components/ui/BaseDivider.jsx";

const LOCK_MESSAGE_TOKEN = "usuario bloqueado temporalmente";

// Fases del login con FluxGuardian. Las dos son ciegas para el usuario si no se
// muestran: en "redirecting" el navegador todavía está saliendo hacia Guardian,
// y en "exchanging" volvió con el código de un solo uso y se está canjeando por
// el JWT — antes ese canje corría mostrando el formulario de login completo,
// como si no estuviera pasando nada.
const GUARDIAN_REDIRECTING = "redirecting";
const GUARDIAN_EXCHANGING = "exchanging";

// El canje reemplaza el formulario por el spinner, así que necesita fallar solo:
// sin tope, un request colgado dejaba al usuario mirando el spinner sin forma de
// reintentar. Al vencer, cae en el catch y vuelve el formulario con el error.
const EXCHANGE_TIMEOUT_MS = 15_000;

// El código sirve para arrancar en "exchanging" sólo si además no vino un error:
// con las dos cosas en la URL el efecto muestra el error y corta, y el spinner
// se quedaría colgado.
function hasPendingOidcExchange() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("oidc_code")) && !params.get("error");
}

function LoginHeader() {
  return (
    <div className="flex flex-col items-center mb-8 gap-3">
      <div className="w-26 h-26 ">
        <img src="/logo-x.png" alt="" width="32" height="32" className="w-26 h-26" />
      </div>
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white">Centro de Agentes Flux</h1>
        <p className="mt-1 text-sm text-slate-400">Ingresá con tu usuario de agente para continuar.</p>
      </div>
    </div>
  );
}

function Spinner({ className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "inline-block animate-spin rounded-full border-2 border-current border-t-transparent",
        className
      ].join(" ")}
    />
  );
}

function isLockMessage(message) {
  return typeof message === "string" && message.toLowerCase().includes(LOCK_MESSAGE_TOKEN);
}

function formatLockUntil(lockedUntil) {
  if (!lockedUntil) {
    return null;
  }

  const lockDate = new Date(lockedUntil);
  if (Number.isNaN(lockDate.getTime())) {
    return null;
  }

  return lockDate.toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    dateStyle: "short",
    timeStyle: "short"
  });
}

export default function LoginPage() {
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [lockState, setLockState] = useState({ locked: false, lockedUntil: null });
  const [loading, setLoading] = useState(false);
  // El código de Guardian se lee en el PRIMER render, no en el efecto: useEffect
  // corre después del paint, así que inicializarlo acá es lo que evita que
  // parpadee el formulario de login justo cuando el usuario vuelve de Guardian.
  const [guardianPhase, setGuardianPhase] = useState(() => (hasPendingOidcExchange() ? GUARDIAN_EXCHANGING : null));
  const navigate = useNavigate();

  const exchangingGuardian = guardianPhase === GUARDIAN_EXCHANGING;
  const redirectingToGuardian = guardianPhase === GUARDIAN_REDIRECTING;

  function clearAuthFeedback() {
    setError(null);
    setLockState({ locked: false, lockedUntil: null });
  }

  function applyAuthError(message, lockedUntil = null) {
    const finalMessage = typeof message === "string" && message.trim()
      ? message.trim()
      : "Credenciales inválidas";
    const lockDetected = isLockMessage(finalMessage) || Boolean(lockedUntil);
    setError(finalMessage);
    setLockState({
      locked: lockDetected,
      lockedUntil: lockDetected ? lockedUntil : null
    });
  }

  const lockUntilLabel = formatLockUntil(lockState.lockedUntil);
  const fieldError = lockState.locked ? null : error;

  useEffect(() => {
    if (localStorage.getItem("agent_token")) {
      navigate("/", { replace: true });
      return;
    }

    // Retorno desde FluxGuardian OIDC
    const params = new URLSearchParams(window.location.search);
    const oidcCode = params.get("oidc_code");
    const oidcError = params.get("error");

    if (oidcError) {
      applyAuthError(decodeURIComponent(oidcError));
      setGuardianPhase(null);
      window.history.replaceState({}, "", "/login");
      return;
    }

    // Canje del código de un solo uso por el JWT (el token ya no viaja en la URL).
    if (oidcCode) {
      window.history.replaceState({}, "", "/login");
      axios.post("/auth/oidc/exchange", { code: oidcCode }, { timeout: EXCHANGE_TIMEOUT_MS })
        .then(({ data }) => {
          localStorage.setItem("agent_token", data.token);
          setUser(data.user);
          navigate("/", { replace: true });
        })
        .catch(() => {
          localStorage.removeItem("agent_token");
          applyAuthError("Error al verificar sesión");
          // Sale el spinner y vuelve el formulario, ya con el error a la vista.
          setGuardianPhase(null);
        });
    }
  }, [navigate, setUser]);

  // Volver con el botón "atrás" desde Guardian puede restaurar esta página desde
  // el bfcache: ahí el estado de React sobrevive y el efecto no vuelve a correr,
  // así que el spinner quedaría colgado para siempre.
  useEffect(() => {
    function handlePageShow(event) {
      if (event.persisted) setGuardianPhase(null);
    }
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    clearAuthFeedback();

    try {
      setLoading(true);
      const { data } = await axios.post("/auth/login", { email, password });
      localStorage.setItem("agent_token", data.token);
      setUser(data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const message = err.response?.data?.message || "Credenciales inválidas";
      const lockedUntil = err.response?.data?.lockedUntil || null;
      applyAuthError(message, lockedUntil);
    } finally {
      setLoading(false);
    }
  }

  // Vuelta de Guardian: mientras se canjea el código por el JWT no se muestra el
  // formulario. El usuario ya se autenticó allá; lo único que falta es que
  // termine de cargar, y ofrecerle de nuevo "Ingresar" sólo confunde.
  if (exchangingGuardian) {
    return (
      <div className="w-full max-w-md mx-auto">
        <LoginHeader />
        <CardBox className="shadow-xl">
          <div
            role="status"
            aria-live="polite"
            className="flex flex-col items-center gap-3 px-6 py-12 text-center"
          >
            <Spinner className="h-8 w-8 text-blue-500" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Verificando tu sesión…
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Estamos confirmando tus datos con FluxGuardian.
            </p>
          </div>
        </CardBox>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <LoginHeader />

      <CardBox className="shadow-xl">
        <form onSubmit={handleSubmit} className="p-6 space-y-2">
          {lockState.locked && (
            <div className="login-lock-banner" role="alert" aria-live="polite">
              <p className="login-lock-banner-title">Acceso temporalmente bloqueado</p>
              <p className="login-lock-banner-message">{error}</p>
              {lockUntilLabel && (
                <p className="login-lock-banner-until">Disponible nuevamente: {lockUntilLabel}</p>
              )}
            </div>
          )}

          <FormField label="Correo electrónico">
            <FormControl
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agente@flux.com"
              autoComplete="email"
            />
          </FormField>

          <FormField label="Contraseña" error={fieldError || undefined}>
            <FormControl
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </FormField>

          <div className="pt-2">
            <BaseButton
              type="submit"
              color="success"
              label={loading ? "Ingresando…" : "Ingresar"}
              loading={loading}
              disabled={loading || redirectingToGuardian}
              className="w-full justify-center"
            />
          </div>
        </form>

        <div className="px-6 pb-6">
          <BaseDivider />
          {/* Sigue siendo un <a> con href: la navegación la hace el navegador y
              no se cancela. El onClick sólo pinta el estado, porque salir hacia
              Guardian puede tardar y el click quedaba sin ninguna respuesta
              visible. pointer-events-none evita además el doble click. */}
          <a
            href="/auth/oidc/login"
            onClick={() => setGuardianPhase(GUARDIAN_REDIRECTING)}
            aria-disabled={redirectingToGuardian}
            className={[
              "flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5",
              "text-sm font-medium border transition-colors",
              "border-slate-300 dark:border-slate-600",
              "text-gray-700 dark:text-gray-300",
              "hover:bg-gray-50 dark:hover:bg-slate-800",
              redirectingToGuardian ? "pointer-events-none opacity-70" : "",
            ].join(" ")}
          >
            {redirectingToGuardian && <Spinner className="h-4 w-4" />}
            {redirectingToGuardian ? "Redirigiendo a FluxGuardian…" : "Ingresar con FluxGuardian"}
          </a>
        </div>
      </CardBox>
    </div>
  );
}
